import {UnitAvailability} from './UnitAvailability';
import {calculateAvailability, isMinStayMet, getAvailabilityObject} from './calculateAvailability';
import {
    isEqual,
    isPast,
    isBefore,
    areBothDatesValid
} from './dateHelper';
import {
    isDateError
} from './errorEvents';
import differenceInDays from 'date-fns/difference_in_days';

function listingIsAvailable(arriveDateText, departDateText, arrivalData, departData) {
    const checkinObj = arrivalData.checkin[arriveDateText] || {};
    const checkoutObj = departData.checkout[departDateText] || {};

    return checkinObj && checkoutObj && !checkinObj.booked && !checkinObj.disabled && !checkoutObj.disabled && !checkoutObj.previousDayBooked && !checkoutObj.unfocused;
}

function hasMinStayError(arriveDateText, departDateText, arrivalData, departData, defaultMinStay) {
    const checkinObj = arrivalData.checkin[arriveDateText] || {};
    const checkoutObj = departData.checkout[departDateText] || {minStayMet: isMinStayMet(differenceInDays(departDateText, arriveDateText), defaultMinStay)};

    return checkinObj && checkoutObj && !checkoutObj.minStayMet;
}

function hasMinPriorNotifyError(arriveDateText, arrivalData, currentDate, ua) {
    const beginDate = ua.getBeginDate();
    const beginDateOffsetFromToday = differenceInDays(currentDate, beginDate.text);
    const dateIndex = ua.length;
    const checkinObj = arrivalData.checkin[arriveDateText] || {minPriorNotifyMet: ua.minPriorNotifyMet(dateIndex, beginDateOffsetFromToday)};

    return checkinObj && !checkinObj.minPriorNotifyMet;
}

function hasSameDayArrivalError(currentDate, arriveDateText) {
    return isEqual(currentDate, arriveDateText);
}

function hasDates({arriveDateText = null, departDateText = null}) {
    return !!arriveDateText && !!departDateText;
}

function hasEmptyAvailability(availabilityCalendar = {}) {
    const availability = availabilityCalendar.availability ? availabilityCalendar.availability : {};

    return Object.keys(availability).length === 0;
}

/* eslint-disable complexity */
function hasValidDates({
    currentDate = null,
    arriveDateText = null,
    departDateText = null,
    availabilityCalendar = {},
    error = {}, // most likely a quote error
    skipAvailabilityValidation = false
}) {
    const availabilityObject = getAvailabilityObject(availabilityCalendar);
    const ua = new UnitAvailability(availabilityObject);
    const bothDatesExist = hasDates({arriveDateText, departDateText});
    const bothDatesValidDates = areBothDatesValid(arriveDateText, departDateText);
    const arrivalDateInPast = isPast(arriveDateText);
    const unorderedDates = !isBefore(arriveDateText, departDateText);
    const formattingError = !bothDatesValidDates || arrivalDateInPast || unorderedDates;
    const isEmptyAvailability = hasEmptyAvailability(availabilityCalendar);

    let errorObj = {
        datesValid: false,
        datesEmpty: !bothDatesExist,
        hasMinStayError: null,
        hasQuoteError: null,
        hasSameDayArrivalError: null,
        hasUnavailableDates: null,
        hasFormattingError: formattingError
    }

    if (!bothDatesExist || formattingError) {
        return errorObj;
    }

    if (isEmptyAvailability || skipAvailabilityValidation) {
        errorObj.datesValid = true;
        return errorObj;
    }

    const arrivalData = calculateAvailability(availabilityCalendar, currentDate);
    const departData = calculateAvailability(availabilityCalendar, currentDate, arriveDateText);

    const datesAvailable = listingIsAvailable(arriveDateText, departDateText, arrivalData, departData);
    const minStayError = hasMinStayError(arriveDateText, departDateText, arrivalData, departData, availabilityCalendar.availability.minStayDefault);
    const minPriorNotifyError = hasMinPriorNotifyError(arriveDateText, arrivalData, currentDate, ua);
    const sameDayArrivalError = hasSameDayArrivalError(currentDate, arriveDateText);
    const quoteError = isDateError(error);

    const datesValid = datesAvailable && !minStayError && !sameDayArrivalError && !quoteError && !minPriorNotifyError;

    const resultsObj = {
        datesValid,
        datesEmpty: !bothDatesExist,
        hasMinStayError: minStayError,
        hasMinPriorNotifyError: minPriorNotifyError,
        hasQuoteError: quoteError,
        hasSameDayArrivalError: sameDayArrivalError,
        hasUnavailableDates: !datesAvailable,
        hasFormattingError: formattingError
    };

    return resultsObj;
}
/* eslint-enable complexity */

export default listingIsAvailable;
export {
    hasValidDates,
    listingIsAvailable,
    hasEmptyAvailability,
    hasMinStayError,
    hasMinPriorNotifyError,
    hasSameDayArrivalError
};
